// ProfilePage.js - Fixed with correct supervisor field names
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Spin, 
  Upload,
  Image,
  App
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  SolutionOutlined, 
  KeyOutlined,
  UploadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  IdcardOutlined,
  BankOutlined,
  SaveOutlined,
  RollbackOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { useNotificationApi } from '../context/NotificationContext';
import api from '../utils/api';
import dayjs from 'dayjs';
import './ProfilePage.css';

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
const DEFAULT_TIME_FORMAT = 'hh:mm A';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const ProfilePage = () => {
  const { user, loading: authLoading, refreshUser } = useAuth(); 
  const { appSettings } = useAppContext();
  const notificationApi = useNotificationApi();
  
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [formInitializing, setFormInitializing] = useState(true);
  const [signatureFile, setSignatureFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [removingSignature, setRemovingSignature] = useState(false);

  const dateFormat = appSettings?.dateFormat || DEFAULT_DATE_FORMAT;
  const timeFormat = appSettings?.timeFormat || DEFAULT_TIME_FORMAT;

  const showError = useCallback((title, content, key = 'profileError') => {
    notificationApi.destroy(key);
    notificationApi.error({
      key,
      message: title,
      description: content,
      duration: 4.5, 
      placement: 'topRight',
      style: { width: 400, zIndex: 10000 }
    });
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'profileSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3, 
      placement: 'topRight',
      style: { width: 400, zIndex: 10000 }
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300; 
    const minDisplayTime = 500; 
    const startTime = Date.now(); 
    
    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0, 
        placement: 'topRight',
        style: { width: 400, zIndex: 10000 }
      });
    }, delay);

    return () => {
      clearTimeout(timer); 
      const elapsed = Date.now() - startTime; 
      const remaining = Math.max(0, minDisplayTime - elapsed);
      setTimeout(() => {
        if (dismissNotification) { 
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Function to get supervisor name from user object
  const getSupervisorName = useCallback((userData) => {
    if (!userData) return 'Not Assigned';
    
    // Based on your mapUser function, the fields are:
    // supervisorFirstName: user.supervisor_first_name
    // supervisorLastName: user.supervisor_last_name
    
    const firstName = userData.supervisorFirstName || userData.supervisor_first_name;
    const lastName = userData.supervisorLastName || userData.supervisor_last_name;
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (userData.supervisorId || userData.supervisor_id) {
      return 'Supervisor (Name not available)';
    }
    
    return 'Not Assigned';
  }, []);

  useEffect(() => {
    if (user) {
      console.debug('ProfilePage: Initializing form with user data:', user);
      const formData = {
        firstName: user.firstName || user.first_name, 
        lastName: user.lastName || user.last_name,
        email: user.email, 
        designation: user.designation,
        phoneNumber: user.phoneNumber || user.phone_number
      };
      form.setFieldsValue(formData);
      setSignatureUrl(user.signatureUrl);
      
      setFormInitializing(false);
    }
  }, [user, form]);

  const handleFileChange = ({ file }) => {
    if (file) {
      if (file.status === 'removed') {
        setSignatureFile(null);
        setUploading(false);
        return;
      }
      
      if (file.status === 'uploading') {
        setUploading(true);
        return;
      }
      
      if (file.status === 'done') {
        setUploading(false);
      }
      
      if (!['image/jpeg', 'image/tiff'].includes(file.type)) {
        showError('Invalid File', 'Please upload a JPG or TIFF file.');
        setSignatureFile(null);
        setUploading(false);
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        showError('File Too Large', 'Signature file must be under 2MB.');
        setSignatureFile(null);
        setUploading(false);
        return;
      }
      setSignatureFile(file);
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSignatureFile(null);
    setRemovingSignature(true);
    setSignatureUrl(null);
  };

  const handleSubmit = useCallback(async (values) => {
    setSubmitting(true);
    const loadingKey = 'profileUpdateLoading';
    const hideLoading = showLoading(loadingKey, 'Updating profile...');

    try {
      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('email', values.email);
      formData.append('designation', values.designation || '');
      formData.append('phoneNumber', values.phoneNumber || '');

      if (values.newPassword) {
        if (values.newPassword.length < 8) {
          throw new Error('New password must be at least 8 characters long.');
        }
        if (values.newPassword.length > 32) {
          throw new Error('New password cannot exceed 32 characters.');
        }
        if (values.newPassword !== values.confirmPassword) {
          throw new Error('New password and confirm password do not match.');
        }
        formData.append('newPassword', values.newPassword);
      }

      if (removingSignature) {
        formData.append('signature', 'null');
        console.log('Sending signature: null (removing signature)');
      } else if (signatureFile) {
        formData.append('signature', signatureFile);
        console.log('Uploading new signature file');
      }

      console.log('ProfilePage: Submitting formData:', [...formData.entries()]);
      const response = await api.put('/api/users/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });

      hideLoading();
      showSuccess('Profile Updated', 'Your changes have been saved successfully');
      console.log('Profile update response:', response.data);

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }

      // Update signature URL from response
      if (response.data.data) {
        const updatedData = response.data.data;
        
        if (updatedData.signatureUrl !== undefined) {
          if (updatedData.signatureUrl === null || updatedData.signatureUrl === '') {
            setSignatureUrl(null);
            console.log('Signature removed successfully');
          } else {
            setSignatureUrl(updatedData.signatureUrl);
            console.log('Signature updated to:', updatedData.signatureUrl);
          }
        }
      }
      
      // Refresh user data
      if (refreshUser) {
        console.log('ProfilePage: Calling refreshUser...');
        await refreshUser();
        console.log('ProfilePage: refreshUser completed');
      }

      form.setFieldsValue({ newPassword: '', confirmPassword: '' });
      form.resetFields(['newPassword', 'confirmPassword']);
      
      setSignatureFile(null);
      setRemovingSignature(false);
      
    } catch (error) {
      hideLoading();
      console.error('Profile update failed:', error);
      
      setRemovingSignature(false);
      setSignatureUrl(user?.signatureUrl || null);
      
      const message = error.response?.data?.message || 
                     error.response?.data?.error ||
                     (error.code === 'ERR_NETWORK' ? 'Network error. Please check your connection or try again later.' : 
                      error.code === 'INVALID_INPUT' ? 'Please provide valid first name, last name, and email.' :
                      error.code === 'INVALID_EMAIL' ? 'Please provide a valid email address.' :
                      error.code === 'INVALID_PASSWORD' ? 'Password must be at least 8 characters.' :
                      error.code === 'DUPLICATE_EMAIL' ? 'This email is already associated with another account.' :
                      error.code === 'USER_NOT_FOUND' ? 'User not found. Please log in again.' :
                      error.code === 'INVALID_FILE_TYPE' ? 'Please upload a JPG or TIFF file.' :
                      error.code === 'FILE_TOO_LARGE' ? 'Signature file must be under 2MB.' :
                      error.code === 'DATA_TOO_LONG' ? 'One or more input fields are too long.' :
                      error.code === 'INVALID_REFERENCE' ? 'Invalid reference data (e.g., organization or supervisor).' :
                      error.message === 'Unauthorized: Please log in again.' ? 'Session expired. Please log in again.' :
                      error.message.includes('401') ? 'Session expired. Please log in again.' :
                      'Failed to update profile. Please try again.');
    
      showError('Update Failed', message);
    
      if (error.response?.status === 401 || error.message.includes('401')) {
        console.log('Authentication error, please login again');
      }
    } finally {
      setSubmitting(false);
    }
  }, [form, refreshUser, showLoading, showSuccess, showError, signatureFile, user?.signatureUrl, removingSignature]);

  if (authLoading || formInitializing) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="loading-container">
        <p>Failed to load profile data. Please try again later.</p>
      </div>
    );
  }

  // Get supervisor name
  const supervisorName = getSupervisorName(user);

  return (
    <App>
      <div className="profile-page">
        <section className="profile-header">
          <h1 className="profile-title">My Profile</h1>
          <span className="profile-subtitle">
            Manage your personal information and account settings
          </span>
        </section>

        <div className="profile-content">
          <div className="profile-form-container">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="profile-form"
            >
              <div className="form-section">
                <h3 className="form-section-title">Personal Information</h3>
                
                <div className="form-grid">
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{ required: true, message: 'First name is required' }]}
                  >
                    <Input 
                      prefix={<UserOutlined />} 
                      placeholder="Enter first name" 
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{ required: true, message: 'Last name is required' }]}
                  >
                    <Input 
                      prefix={<UserOutlined />} 
                      placeholder="Enter last name" 
                      size="large"
                    />
                  </Form.Item>

                  <div className="form-grid-full">
                    <Form.Item 
                      name="email" 
                      label="Email Address"
                      rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
                    >
                      <Input 
                        prefix={<MailOutlined />} 
                        placeholder="your.email@company.com" 
                        size="large"
                      />
                    </Form.Item>
                  </div>

                  <Form.Item 
                    name="designation" 
                    label="Designation"
                  >
                    <Input 
                      prefix={<SolutionOutlined />} 
                      placeholder="Your job title" 
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item 
                    name="phoneNumber" 
                    label="Phone Number"
                    rules={[{ pattern: /^[+]?[\s\d()-]*$/, message: 'Please enter a valid phone number' }]}
                  >
                    <Input 
                      prefix={<PhoneOutlined />} 
                      placeholder="+1 234 567 8901" 
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Change Password</h3>
                <p style={{ color: '#718096', marginBottom: '20px', fontSize: '14px' }}>
                  Leave blank to keep current password
                </p>
                
                <div className="form-grid">
                  <Form.Item
                    name="newPassword"
                    label="New Password"
                    rules={[
                      { min: 8, message: 'Password must be 8-32 characters' },
                      { max: 32, message: 'Password must be 8-32 characters' }
                    ]}
                    hasFeedback
                  >
                    <Input.Password 
                      prefix={<KeyOutlined />} 
                      placeholder="Enter new password" 
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="confirmPassword"
                    label="Confirm Password"
                    dependencies={['newPassword']}
                    hasFeedback
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const newPass = getFieldValue('newPassword');
                          if (!newPass) { 
                            return Promise.resolve();
                          }
                          if (value && newPass === value) { 
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Passwords must match')); 
                        }
                      })
                    ]}
                  >
                    <Input.Password 
                      prefix={<SafetyOutlined />} 
                      placeholder="Confirm new password" 
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Signature</h3>
                
                <div className="signature-section">
                  {signatureUrl && !removingSignature && (
                    <div className="signature-preview">
                      <Image
                        src={signatureUrl?.startsWith('http') ? signatureUrl : `${API_BASE_URL}${signatureUrl}`}
                        alt="User Signature"
                        className="signature-image"
                        fallback="/path/to/fallback-image.png"
                        preview={false}
                      />
                      <div className="signature-actions">
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={handleRemoveFile}
                          danger
                          disabled={submitting || uploading}
                          style={{ marginBottom: '8px' }}
                        >
                          Remove Signature
                        </Button>
                        <p style={{ color: '#718096', fontSize: '13px', margin: '0' }}>
                          Current signature
                        </p>
                      </div>
                    </div>
                  )}

                  {!signatureUrl && user.signatureUrl && removingSignature && (
                    <div className="signature-preview" style={{ backgroundColor: '#fff5f5', borderColor: '#fed7d7' }}>
                      <div style={{ padding: '20px', textAlign: 'center', width: '100%' }}>
                        <p style={{ color: '#c53030', marginBottom: '8px' }}>
                          <DeleteOutlined /> Signature marked for removal
                        </p>
                        <p style={{ color: '#718096', fontSize: '13px' }}>
                          Click "Save Changes" to remove signature permanently
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="upload-area">
                    <Upload
                      accept="image/jpeg,image/tiff"
                      beforeUpload={() => false}
                      onChange={handleFileChange}
                      fileList={signatureFile ? [{ uid: '-1', name: signatureFile.name, status: 'done' }] : []}
                      maxCount={1}
                      onRemove={() => {
                        setSignatureFile(null);
                        if (removingSignature) {
                          setRemovingSignature(false);
                          setSignatureUrl(user?.signatureUrl || null);
                        }
                      }}
                      disabled={submitting || uploading}
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        disabled={submitting || uploading}
                        size="large"
                      >
                        {signatureUrl || user.signatureUrl ? 'Replace Signature' : 'Upload Signature'}
                      </Button>
                    </Upload>
                    <span className="upload-hint">
                      JPG or TIFF format only • Maximum file size: 2MB
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Account Information</h3>
                
                <div className="account-info-grid">
                  <div className="info-item">
                    <Form.Item label="User Type">
                      <Input 
                        value={user.userType ? user.userType.replace(/_/g, ' ').toUpperCase() : 'N/A'} 
                        readOnly 
                        disabled 
                        addonBefore={<IdcardOutlined />}
                      />
                    </Form.Item>
                  </div>

                  <div className="info-item">
                    <Form.Item label="Role">
                      <Input 
                        value={user.roleName || 'N/A'} 
                        readOnly 
                        disabled 
                        addonBefore={<SolutionOutlined />}
                      />
                    </Form.Item>
                  </div>

                  <div className="info-item">
                    <Form.Item label="Organization">
                      <Input 
                        value={user.organizationName || 'N/A'} 
                        readOnly 
                        disabled 
                        addonBefore={<BankOutlined />}
                      />
                    </Form.Item>
                  </div>

                  <div className="info-item">
                    <Form.Item label="Supervisor">
                      <Input 
                        value={supervisorName} 
                        readOnly 
                        disabled 
                        addonBefore={<CrownOutlined />}
                      />
                    </Form.Item>
                  </div>

                  <div className="info-item">
                    <Form.Item label="Account Status">
                      <Input 
                        value={user.isActive ? 'Active' : 'Inactive'} 
                        readOnly 
                        disabled 
                        addonBefore={user.isActive ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                        className={user.isActive ? 'status-active' : 'status-inactive'}
                      />
                    </Form.Item>
                  </div>

                  <div className="info-item">
                    <Form.Item label="Member Since">
                      <Input 
                        value={user.createdAt ? dayjs(user.createdAt).format(`${dateFormat} ${timeFormat}`) : 'N/A'} 
                        readOnly 
                        disabled 
                        addonBefore={<ClockCircleOutlined />}
                      />
                    </Form.Item>
                  </div>

                  {user.lastLogin && (
                    <div className="info-item">
                      <Form.Item label="Last Login">
                        <Input 
                          value={dayjs(user.lastLogin).format(`${dateFormat} ${timeFormat}`)} 
                          readOnly 
                          disabled 
                          addonBefore={<ClockCircleOutlined />}
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>
              </div>

              <div className="profile-actions">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={submitting}
                  size="large"
                  icon={<SaveOutlined />}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  size="large"
                  icon={<RollbackOutlined />}
                  onClick={() => {
                    const formData = {
                      firstName: user.firstName || user.first_name,
                      lastName: user.lastName || user.last_name,
                      email: user.email, 
                      designation: user.designation,
                      phoneNumber: user.phoneNumber || user.phone_number,
                      newPassword: '', 
                      confirmPassword: ''
                    };
                    form.setFieldsValue(formData);
                    form.resetFields(['newPassword', 'confirmPassword']);
                    setSignatureFile(null);
                    setSignatureUrl(user.signatureUrl);
                    setRemovingSignature(false);
                  }}
                  disabled={submitting || uploading}
                >
                  Reset
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </App>
  );
};

export default ProfilePage;